/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FilterValue, RefinementProps } from '../types';

/**
 * Category (`cgid`) refinement — live NM's Category options are plain links
 * with counts, not checkboxes (search-3a-comparison.md "Facets + sort").
 *
 * THE EXPAND AFFORDANCE IS SCOPED TO ONE SURFACE OF ONE BRAND — NM's SEARCH
 * rail. It is NOT a brand trait, and reading it as one is what kept NM's PLP
 * collapsed while live paints it open:
 *
 *   live NM SEARCH  9 of 9 category rows carry a `+`, an 11px `<span>` painting
 *             an 18x18 plus glyph from a background-image data URI. Confirmed
 *             structurally in search-d-1280/App.tsx, group bounded 40994-54859:
 *             nine `<li>` and NO nested `<ul>`.
 *   live NM PLP     0 of 16 child rows carry one, and no glyph occurs anywhere
 *             in the group (nm-plp-d-1280/App.tsx 39140-46094).
 *   live BG   0 rows carry one on EITHER surface.
 *
 * The class names once cited here (`.faceted-left-nav__filter__status_collapsed`,
 * `.category-section-filter`) came from live's own stylesheets and CANNOT be
 * checked against these captures: the decompiler strips live's semantic class
 * names, and all three return zero occurrences in App.tsx. Identify rows here
 * by structure and copy instead — see `childMode` below for the bounded reads.
 *
 * IT IS ALSO GATED ON DATA, which is the half the issue was unsure about.
 * SCAPI's `ProductSearchRefinementValue` carries an optional hierarchical
 * `values[]`, and this catalogue populates it: `?q=shoe` returns Shoes with 8
 * children (Sandals, Booties, Mules, Ballerina Flats, Boots, Espadrilles, …)
 * while `?q=bag` returns Handbags and Men with no `values` key at all. Rows
 * without children get no control, exactly as a leaf should — so the `+` is
 * never drawn over data that cannot expand.
 *
 * The expander is a real `<button>` beside the filter link rather than a span
 * inside it. Live nests the glyph inside the row's own `role="button"`, which
 * is a nested-interactive a11y fault; keeping them siblings costs nothing
 * visually and keeps both actions reachable.
 */
export default function RefineCategory({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
    variant = 'card',
    allResultsRow = false,
    allResultsCount,
    anyCategorySelected = false,
    onClearCategory,
    surface = 'plp',
}: RefinementProps & {
    /** See `CategoryRefinements`' prop of the same name for the evidence. */
    allResultsRow?: boolean;
    allResultsCount?: number;
    anyCategorySelected?: boolean;
    onClearCategory?: () => void;
    /** See `CategoryRefinements`' prop of the same name for the evidence. */
    surface?: 'plp' | 'search';
}): ReactElement {
    const { t } = useTranslation('categoryRefinements');
    const isRail = variant === 'rail';
    const isSearch = surface === 'search';
    /* Hoisted so the bold predicate can be derived from it rather than restated:
       `expander` is NM's search rail and nothing else, and it is the one place
       where bold does NOT mean selected. See `boldMeansSelected` on CategoryRow. */
    const childMode: 'expander' | 'inline' | 'none' = isSearch ? (isRail ? 'expander' : 'none') : 'inline';

    /**
     * PER-OPTION COUNTS ARE A PROPERTY OF THE SURFACE, on both brands alike.
     * Bounded reads of one facet group per capture, each terminated by the
     * FOLLOWING group's own heading rather than by a label further down:
     *
     *   BG search  bg-search-d-1024 12421-17955  SIX  (3749) (3227) (313) …
     *   NM search  search-d-1280    40994-54859  NINE ( 8890 ) ( 1534 ) ( 499 ) …
     *   BG PLP     bg-plp-d-1280    41046-48281  ZERO
     *   NM PLP     nm-plp-d-1280    39140-46094  ZERO — the only parenthesised
     *              runs in range are `(211, 211, 211)` divider colours
     *
     * NM WRITES ITS COUNT WITH INNER SPACES — `( 8890 )`, not `(8890)`. An
     * earlier pass reported NM search as carrying none, because the pattern
     * behind that reading required a digit immediately after the parenthesis
     * and so could not match. The zero was a property of the query, not of the
     * page; both NM numbers here come from a re-read with a permissive pattern,
     * and BG's PLP zero was re-confirmed under the same permissive pattern
     * rather than inherited from the stricter one.
     */
    const showCounts = isSearch;

    /**
     * Live BG's PLP rail lists children ALPHABETICALLY — Activewear, Coats &
     * Jackets, Dresses, Evening Gowns, Jeans, Lingerie & Sleepwear, Pants &
     * Shorts, Skirts, Sweaters, Swimwear & Coverups, Tops
     * (bg-plp-d-1280/App.tsx, first child 42959, last 47569). SCAPI hands them
     * back hit-count descending, so the order has to be imposed here.
     *
     * TWO EXCLUSIONS, both measured rather than assumed:
     *
     *   NM          live NM's PLP rail reads Dresses, Tops, Jackets & Blazers,
     *               Coats, Sweaters, Skirts, Jeans, Pants, Matching Sets,
     *               Tailored Suiting, ... — sixteen children in a merchandised
     *               order that is definitively NOT alphabetical
     *               (nm-plp-d-1280/App.tsx from 39140).
     *   BG SEARCH   live's search rail orders its rows by hit count descending
     *               — (3749) (3227) (313) (197) (1) (1), bg-search-d-1024 from
     *               12421. Those rows are all TOP-LEVEL values, so live's
     *               search group has no child list to compare against ours at
     *               all; alphabetical there would be an extrapolation from the
     *               PLP onto a group whose only visible ordering signal points
     *               the other way. SCAPI already returns hit-count descending,
     *               so leaving search alone invents nothing.
     *
     * Sorting either would be a regression, so the sort is scoped to the one
     * list it was actually measured on: BG's PLP child list.
     */
    const orderChildren = (list: FilterValue[]): FilterValue[] =>
        isRail || isSearch ? list : [...list].sort((a, b) => (a.label || a.value).localeCompare(b.label || b.value));

    /**
     * ONE LIST, ONE RHYTHM — the `All Results` row is a MEMBER of the value
     * list, not a heading over it.
     *
     * Live BG's search rail is a single flat `<ul>` whose every `<li>` carries
     * the IDENTICAL class string, `All Results` included:
     *
     *   bg-search-d-1024/App.tsx from 12421, and -1440 from 24969:
     *     <li className="list-none text-left mt-[16px] mb-[2px] leading-[17.94px]">
     *   six times over — All Results, Women's Clothing, Men, Kids, Handbags,
     *   Home — with no margin, padding or spacing token distinguishing the
     *   first row from the other five. The raw dump agrees at the markup
     *   level: `.reference/bg-search-dress.desktop.html` at 170668 has
     *   `<section class="category-section category-section-filter"><ul>` with
     *   `All Results` as `<li>` #1 and the five categories as its siblings.
     *
     * `gap-2` gave the TOP-LEVEL rows a 44px pitch while the nested child rows
     * sat at 36px, so the first row read as a label over a tighter list under
     * it — the owner's "ours kinda treats All results like a header separate
     * from the cat list under". Measured at 1280 BG `?q=women`, row tops
     * 361.09 / 405.09 (44) against child tops spaced 36. Dropping the gap on
     * SEARCH puts every row of both levels on one 36px pitch, which is within
     * 1.3px of live's own 16+2+19.32 ≈ 37.3.
     *
     * SCOPED TO SEARCH because the PLP is a different list: `_app.category
     * .$categoryId.tsx` mounts this with `surface="plp"` at :850 and :863, and
     * nothing in either PLP capture measures this rhythm. `mt-2` is the list's
     * own top offset, not a row gap, and is untouched.
     */
    return (
        <ul
            data-testid="refinement-cgid-list"
            className={cn(
                'flex flex-col',
                // Written as one arm per branch, rather than two independent
                // arms, so the PLP string stays byte-identical to what it was
                // (`gap-2 mt-2`, that order) and no snapshot moves for a class
                // reorder that changes nothing.
                !isRail && (isSearch ? 'mt-2' : 'gap-2 mt-2'),
                isRail && 'list-none'
            )}>
            {/* The leading "no category" row. `hidden md:block` is live's own
                `hide-on-mobile`: BG's mobile SRP carries this control as the
                first tab of the scrollable strip (`BgSearchCategoryTabs`) and
                its mobile filter panel is a chevron drill-down with no inline
                values, so an unconditional row would paint it twice at mobile.

                BOLD IS THE SELECTED STATE ON THIS RAIL, not a border. Live's
                active row is `font-bold` with no `border-b`, while every
                inactive sibling carries `border-b` in rgb(255,255,255) — an
                invisible underline that reserves the row so nothing shifts when
                the selection moves (bg-search-d-1024/App.tsx, identical at
                -1440). The count span inherits the weight: live gives it no
                weight class of its own inside the bold button. */}
            {allResultsRow && onClearCategory && (
                <li data-testid="refinement-cgid-all-row" className="hidden md:block">
                    <div className="flex items-center justify-between">
                        <Button
                            variant="link"
                            onClick={onClearCategory}
                            aria-current={anyCategorySelected ? undefined : 'true'}
                            className={cn('flex-1 justify-start', !anyCategorySelected && 'font-bold')}>
                            {t('allResults', { defaultValue: 'All Results' })}
                            {allResultsCount !== undefined && (
                                <span
                                    data-testid="refinement-cgid-count"
                                    className={cn(
                                        'text-(length:--facet-cat-count-font-size) text-(--facet-cat-count-color) pl-(--facet-cat-count-pad-start)',
                                        anyCategorySelected ? 'font-normal' : 'font-bold'
                                    )}>
                                    ({allResultsCount})
                                </span>
                            )}
                        </Button>
                    </div>
                </li>
            )}
            {values.map((value) => (
                <CategoryRow
                    key={`${attributeId}:${value.value}`}
                    value={value}
                    attributeId={attributeId}
                    isFilterSelected={isFilterSelected}
                    toggleFilter={toggleFilter}
                    childMode={childMode}
                    /* BOLD MEANS SELECTED EVERYWHERE EXCEPT NM'S SEARCH RAIL, where it
                       means EXPANDED — see the prop's own note for both derivations. */
                    boldMeansSelected={childMode !== 'expander'}
                    showCounts={showCounts}
                    orderChildren={orderChildren}
                    capChildList={isSearch}
                    expandLabel={t('expandCategory', {
                        category: value.label || value.value,
                        defaultValue: 'Show subcategories of {{category}}',
                    })}
                    collapseLabel={t('collapseCategory', {
                        category: value.label || value.value,
                        defaultValue: 'Hide subcategories of {{category}}',
                    })}
                />
            ))}
        </ul>
    );
}

function CategoryRow({
    value,
    attributeId,
    isFilterSelected,
    toggleFilter,
    childMode,
    boldMeansSelected,
    showCounts,
    orderChildren,
    capChildList,
    expandLabel,
    collapseLabel,
}: {
    value: FilterValue;
    attributeId: string;
    isFilterSelected: RefinementProps['isFilterSelected'];
    toggleFilter: RefinementProps['toggleFilter'];
    /**
     * How a row's SCAPI children are reached. THIS IS A SURFACE TRAIT, NOT A
     * BRAND ONE — both brands list children inline on a PLP, and the collapse
     * belongs to NM's SEARCH rail alone:
     *
     *   `expander`  a `+` per row, children COLLAPSED until clicked.
     *   `inline`    no glyph; children listed under their parent, always shown.
     *   `none`      no glyph and NO child list at all — a flat column of
     *               top-level rows. BG's SEARCH rail, and nothing else.
     *
     * Each group below was bounded from its own heading to the FOLLOWING
     * group's heading, and read past the last member expected:
     *
     *   NM PLP     nm-plp-d-1280 39140-46094. Parent anchor `Clothing`, itself
     *              `font-bold`, then a NESTED `<ul>` holding SIXTEEN child
     *              anchors inline — `Dresses` at 40747 through
     *              `Jumpsuits & Rompers` at 45313. No expander glyph occurs
     *              anywhere inside the group.
     *   BG PLP     bg-plp-d-1280 41046-48281. Same shape: bold parent `Clothing`
     *              at 42450, ELEVEN child anchors from `Activewear` at 42959
     *              through `Tops` at 47569, twelve anchors and zero glyphs.
     *   NM search  search-d-1280 40994-54859. NINE top-level rows, `Women's
     *              Clothing` at 42027 through `Jewelry` at 52942, and NO nested
     *              `<ul>` at all. Every row carries an 11px span painting an
     *              18x18 plus from a background-image data URI. This is the one
     *              group that collapses.
     *   BG search  FLAT, and this is the arm `none` exists for. Re-derived
     *              2026-08-24 by bounding the Category group from its own
     *              heading to the FOLLOWING group's heading and counting tags,
     *              not by matching a name:
     *                bg-search-d-1024 [12422:17956]  1 <ul>, 0 nested, 6 buttons
     *                  All Results (3749) · Women's Clothing (3227) · Men (313)
     *                  · Kids (197) · Handbags (1) · Home (1)
     *                bg-search-d-1440 [24067:30121]  1 <ul>, 0 nested, 7 buttons
     *                  All Results (3861) · Women's Clothing (3311) · Men (331)
     *                  · Kids (199) · Home (3) · Handbags (2) · Shoes (1)
     *              `backgroundImage` occurs 0 times in either group, so there is
     *              no plus glyph either — this is not a collapsed expander, it
     *              is a list with no second level. The owner's own full-width
     *              screenshot of live BG `?q=dress` (2026-08-23) is a third
     *              sample of the same six rows.
     *
     *              POPULATION, because a capture cannot establish a never: BG's
     *              other three desktop search captures (-768, -1280,
     *              facet-d-1024) carry the `Category` heading with DESIGNER
     *              immediately after it and 0 <ul> in the following 2500 chars —
     *              the group is collapsed there, so they are neutral rather than
     *              corroborating, and the expanded population is exactly two
     *              captures plus the screenshot.
     *
     *              WHAT MAKES IT MORE THAN THREE SAMPLES: the falsifying case
     *              would be a live BG SRP row whose category HAS children.
     *              `Women's Clothing` is that row — the same taxonomy node
     *              renders ELEVEN inline children on live BG's PLP
     *              (bg-plp-d-1280 [41046:48281], 2 <ul>, 12 anchors, re-counted
     *              here) — and live's SRP still paints it flat with no glyph at
     *              3227 and 3311 hits. So live's search rail is flat by
     *              construction, not because the data ran out.
     *
     * THE COMMENT THIS REPLACES CITED THE SEARCH CAPTURES TO JUSTIFY COLLAPSING
     * NM'S PLP. That reading of NM search was correct and is preserved above;
     * it was simply evidence about the other surface. Gating on the variant
     * alone then collapsed a PLP list that live paints open, which is the same
     * defect BG had — a correct measurement applied outside the scope it covers.
     */
    childMode: 'expander' | 'inline' | 'none';
    /**
     * BOLD MEANS SELECTED ON THIS LIST — and it may mean nothing else.
     *
     * Live BG's search rail spends `font-bold` on exactly ONE button, the
     * active one, and marks every inactive sibling with a `border-b` in
     * rgb(255,255,255) instead (bg-search-d-1024/App.tsx from 12421, identical
     * at -1440; the raw dump at 170668 carries the same split as
     * `<button class="active">` against `<button class="">`). The all-row's
     * own comment above already recorded that; what it did not say is that the
     * converse binds too — a row that is NOT selected may not be bold for any
     * other reason.
     *
     * A now-retired `isInlineParent` broke that. It bolded any row with an
     * inline child list, off bg-plp-d-1280/App.tsx:42450 where `Clothing` heads
     * eleven children — a reading that turned out to be under-determined on the
     * PLP too, for the reason written where that constant used to be. On SEARCH
     * it collides with the all-row's selected-bold: on a fresh load nothing is
     * refined, so `All Results` is bold, and the moment SCAPI returns
     * hierarchical `values[]` for a category that row goes bold as well.
     * Measured at 1280 BG before this change — `?q=women` and `?q=dress` and
     * `?q=shoe` all render TWO bold rows, while `?q=bag` and `?q=top`, whose
     * categories have no children, render one. The bold tracked CHILDREN, not
     * selection, which is why it appeared exactly as subcategory data landed.
     *
     * So on search the parent-bold is off and the SELECTED row carries the
     * weight instead. Without the second half nothing would be bold at all
     * once a childless category is picked — `?q=bag` + Handbags reached that
     * state through a trusted click and the rail showed no selection anywhere.
     *
     * NM's search rail is `variant="rail"` (`SEARCH_UX_BY_BRAND.nm`), so it is
     * `expander` mode, where bold means EXPANDED instead — it is THE ONLY
     * exclusion left, and this prop is now simply `childMode !== 'expander'`.
     * Keeping it excluded also keeps `overrides/search.css` off the fence: that
     * file keys NM's row treatment on
     * `[data-variant='rail'] … button:not(.font-bold)`, so a row newly gaining
     * `font-bold` would drop out of it. Both PLPs are out of that rule's reach
     * for an independent reason — they mount `variant="card"`, which
     * `[data-variant='rail']` does not match — which is why widening this flag
     * to the PLP moves weight and touches nothing else.
     */
    boldMeansSelected: boolean;
    /** See `showCounts` in the parent for the four-capture derivation. */
    showCounts: boolean;
    /** See `orderChildren` in the parent — alphabetical on BG, untouched on NM. */
    orderChildren: (list: FilterValue[]) => FilterValue[];
    /**
     * Bound the child list and let it scroll, which live does on its SEARCH rail
     * only. `bg-search-d-1024/App.tsx` at 12421 and `-1440` at 24969 carry the
     * identical two-box structure: a group `<section>` capped at 465px wrapping
     * the value `<ul>`, which is the element that carries both the scroll and
     * the 435px cap. BG's PLP capture has no cap on any facet list, so this is
     * not a rail-wide treatment.
     */
    capChildList: boolean;
    expandLabel: string;
    collapseLabel: string;
}): ReactElement {
    const [isOpen, setIsOpen] = useState(false);
    const isSelected = isFilterSelected(attributeId, value.value);
    // Hierarchical children, when SCAPI returns any. `values` is optional on
    // ProductSearchRefinementValue and this catalogue leaves it off for leaves.
    const children = Array.isArray(value.values) ? value.values : [];
    const hasChildren = children.length > 0;
    const isExpanderMode = childMode === 'expander';
    const isInlineMode = childMode === 'inline';
    /**
     * `inline` has no collapsed state to open, so its children are
     * unconditional; `expander` shows them once opened; `none` never does.
     *
     * THE `none` ARM IS THE [ours-extra] FIX. It was `inline` for BG's search
     * rail purely because that arm was the fallthrough of a two-value switch —
     * `isRail && isSearch ? 'expander' : 'inline'` — so BG search inherited the
     * PLP's shape by default rather than by measurement, and painted an
     * indented child group under the first category that SCAPI returned
     * hierarchical `values[]` for. Live BG's SRP has no second level at all;
     * see the `childMode` prop's bounded per-capture reads.
     *
     * NOTE THIS IS DATA-GATED AND CURRENTLY UNPOPULATED. Measured on the
     * running app at 1440 on 2026-08-24, BG `?q=dress|women|top|bag|shoe|sale`
     * and NM `?q=dress|women|shoe` ALL return `refinement-cgid-children` 0 —
     * SCAPI's search refinements carry no `values[]` today, where a read the
     * previous day found eight children under `Shoes` on `?q=shoe`. Both PLPs
     * still populate (BG `/category/womens-clothing` renders 8 child rows, NM
     * 5), so the plumbing is intact and only the search response changed. The
     * arm is therefore a latent divergence rather than a live one, and removing
     * it costs nothing whether or not the data comes back.
     */
    const showChildren = hasChildren && (isExpanderMode ? isOpen : isInlineMode);
    /**
     * `isInlineParent` USED TO LIVE HERE AND WAS WRONG. It bolded any inline row
     * that HAS CHILDREN, on the strength of `Clothing` reading `font-bold` at
     * 14px/18.2px in bg-plp-d-1280/App.tsx:42450 while its children read
     * `font-normal`. That observation is real and it does not support the rule.
     *
     * EVERY PLP CAPTURE ON DISK IS `womens-clothing` — the ROOT category, where
     * the ancestor and the current category are THE SAME ROW. Checked all four
     * (`{nm,bg}-plp-{d-1280,m-440}/meta.json`): each records
     * `.../c/womens-clothing-cat...`. So "the parent of a child list" and "the
     * category you are on" were indistinguishable in the whole population, and
     * bolding-because-it-has-children was one of two rules that fit equally.
     *
     * A DEEP category separates them, and live picks the other one.
     * `.reference/bg-plp-womens-dresses.desktop.html` (canonical
     * `/c/womens-clothing-clothing-dresses-cat80001`) nests
     * `Clothing > Dresses > 8 leaves`, so BOTH `Clothing` and `Dresses` have
     * children, and exactly one class separates them:
     *
     *   Clothing  <a class="font-stack-default" aria-current="">
     *   Dresses   <a class="left-nav__category active font-stack-default"
     *                aria-current="page">
     *
     * `active` is on the CURRENT category, not on the row with children. Live's
     * own stylesheet turns that into weight, and the ancestor stays light:
     *
     *   .category-level-leftnav .left-nav__category a        { font-weight:400 }  (0,2,1)
     *   .category-level-leftnav .left-nav__category a.active { font-weight:700 }  (0,3,1)
     *
     * NM does the same thing with the same class. Its PLP dump nests
     * `Clothing[active, aria-selected=true] > children[class=""]`
     * (dumps/nm-plp-d-1280/dt-plp-1280-nm-nojs.html), and NM's own PLP bundle
     * declares `.left-nav__category a.active{font-weight:700}`
     * (dt-plp-1280-nm_files/app--crp-category.*.bundle.css). NM's deep state is
     * INFERRED from an identical mechanism — no NM deep-category capture is on
     * disk — while BG's is directly observed.
     *
     * So there is no separate inline-parent rule: on the PLP, as on BG's search
     * column, bold means SELECTED. `boldMeansSelected` is now true for every
     * mode but `expander`, which is why this constant is gone rather than
     * rewritten — `isInlineMode && !boldMeansSelected` is unsatisfiable.
     */

    return (
        <li data-testid="refinement-cgid-row">
            <div className="flex items-center justify-between">
                {/* `flex-1` keeps the control filling its row. Without it the
                    new list structure shrink-wraps the button and BG's rows
                    narrowed from 222px to 102px at 768 — an unintended,
                    unmeasured BG change. It also puts the expander on the
                    trailing edge, which is where live NM has it
                    (`flex justify-between` on the row, search-d-1280:431). */}
                <Button
                    variant="link"
                    onClick={() => toggleFilter(attributeId, value.value)}
                    className={cn(
                        'flex-1 justify-start',
                        isSelected && 'border-foreground/80',
                        // Live's active row IS the bold one — see `boldMeansSelected`.
                        boldMeansSelected && isSelected && 'font-bold',
                        /**
                         * ON NM'S RAIL BOLD MEANS **EXPANDED**, NOT SELECTED —
                         * the opposite meaning to BG's, and the two must not be
                         * unified.
                         *
                         * Live NM search, owner captures 2026-08-22. On a fresh
                         * `?q=dress` load (`… 4.35.44 PM`) all NINE top-level
                         * rows carry `+` and NOT ONE is bold — there is no
                         * "All Results" row and nothing is selected, so a
                         * selected-bold rail would have had to paint something.
                         * After expanding two levels (`… 4.36.10 PM`) exactly
                         * the two rows showing `−` are bold — `Women's Clothing`
                         * and, beneath it, `Dresses` — while all nine of their
                         * collapsed siblings (Tops, Skirts, Swimwear & Coverups,
                         * Jackets, Activewear, Sweaters, Pants, Lingerie &
                         * Shapewear, Sleepwear) stay light. Still nothing
                         * selected. So the whole expanded ancestor chain is
                         * bold, and expansion is the only predicate that fits.
                         *
                         * Ours painted `font-weight: 400` on every row in every
                         * state, expanded included (measured at 1728 on
                         * `?q=shoe` and `?q=dress`).
                         *
                         * This is why `boldMeansSelected` is gated to `!isRail`.
                         * Both brands spend the same property, and they spend it
                         * on different things: BG's rail has no expanders at all
                         * — the owner's BG capture shows `+` on DESIGNER, SIZE,
                         * COLOR and PRICE and on not one category row — so
                         * "expanded" is not even expressible there, and NM has
                         * no "All Results" row for a selected-bold to start on.
                         *
                         * `overrides/search.css:770` already holds the rail's
                         * weight reset off `.font-bold`; until now no rail row
                         * could ever carry that class, so the guard was vacuous.
                         */
                        isExpanderMode && isOpen && 'font-bold'
                    )}>
                    {value.label || value.value}
                    {/* Per-option counts. Live carries them on ONE surface of one
                        brand — BG's search rail — and on neither PLP; see
                        `showCounts` in the parent for the bounded per-capture
                        counts behind that. */}
                    {showCounts && value.hitCount !== undefined && (
                        <span
                            data-testid="refinement-cgid-count"
                            className="font-normal text-(length:--facet-cat-count-font-size) text-(--facet-cat-count-color) pl-(--facet-cat-count-pad-start)">
                            ({value.hitCount})
                        </span>
                    )}
                </Button>
                {isExpanderMode && hasChildren && (
                    <button
                        type="button"
                        data-testid="refinement-cgid-expander"
                        aria-expanded={isOpen}
                        aria-label={isOpen ? collapseLabel : expandLabel}
                        onClick={() => setIsOpen((o) => !o)}
                        className="shrink-0 w-(--facet-cat-expander-size) cursor-pointer leading-none">
                        {isOpen ? '−' : '+'}
                    </button>
                )}
            </div>
            {showChildren && (
                <ul
                    data-testid="refinement-cgid-children"
                    className={cn(
                        'list-none',
                        /**
                         * NM'S SEARCH RAIL INDENTS AND SPACES ITS CHILD LIST;
                         * nothing else here does. Measured off the owner's live
                         * NM captures of 2026-08-22 (`Screenshot … 4.35.44 PM`
                         * fresh load, `… 4.36.10 PM` with Women's Clothing and
                         * Dresses both expanded), sampled through a colour-
                         * managed canvas over the raw pixels.
                         *
                         * DPR IS 2.0, DERIVED PER FILE RATHER THAN ASSUMED. The
                         * anchor is live's own declared level-1 row box, which
                         * is flat across search-d-1024, -1280 and -1920:
                         * `<ul text-[14px] leading-[16px]>`, `<li ml-[4px]
                         * mb-[18px]>`, inner `div[role=button] p-[6px]`
                         * — 6 + 16 + 6 + 18 = 46 CSS px. The fresh-load shot
                         * measures 93 native px between consecutive level-1 ink
                         * tops, EIGHT intervals with zero variance, so
                         * 93 / 46 = 2.02. The BG shot cross-checks it against a
                         * different site's declared box and lands on the same
                         * figure, and 3456 / 2 = 1728 is the stock logical width
                         * of the display these were taken on.
                         *
                         *   level 1 -> level 2 indent   28 native = 14 CSS
                         *     level-1 text minx 169.4 (mean of 9 rows, letters
                         *     W/M/K/H/S/A/H/B/J), level-2 text minx 197.0 (mean
                         *     of 9 rows) — different first letters on both
                         *     sides, so the step is not a glyph artefact.
                         *   level 2 row pitch           77 native = 38.5 CSS
                         *     eight consecutive intervals, zero variance.
                         *   level 1 -> first level 2    85 native = 42.5 CSS
                         *     ONE interval only — weaker than the other two.
                         *
                         * Ours measured 0 indent and a 16 CSS pitch before this
                         * (NM `?q=shoe`, eight children, seven identical 16.0
                         * intervals at 1728), with the first child 28 CSS below
                         * its parent. `ps-[20px]` is 6 + 14: the parent's own
                         * label sits 6px in from the row box because live's
                         * `p-[6px]` is on the row, so 20 puts the child text 14
                         * past the parent's. `pt` and `mb` supply the two gaps.
                         *
                         * WHY THESE ARE LITERALS AND NOT `--facet-cat-*` TOKENS:
                         * the token files are outside this pass's file manifest.
                         * The gate makes that safe rather than merely tolerable —
                         * `isExpanderMode` is `isRail && isSearch`, which is NM's
                         * SRP and nothing else, so no other brand or surface can
                         * reach these values. A `[data-variant='rail']` CSS rule
                         * could NOT have been scoped this way: `PLP_UX_BY_BRAND`
                         * sets NM's PLP to `railVariant: 'rail'` too
                         * (`lib/category/plp-ux.ts:327`), so that selector spans
                         * both NM surfaces. The comment at
                         * `theme/overrides/search.css:753` asserting the rail
                         * variant is "NM's SRP alone" is wrong on that point.
                         * Extracting these three into brand-nm tokens is a
                         * follow-up, not a behaviour change.
                         *
                         * THE INDENT AND THE TOP GAP ARE ON THE CHILD `<li>`,
                         * NOT HERE, AND THAT IS FORCED. This `<ul>` is a direct
                         * child of the row, so `overrides/search.css:684` —
                         * `[data-variant='rail'] [data-testid='refinement-cgid-row'] > *
                         * { padding: var(--facet-cat-row-pad) }` — matches it and
                         * wins. It is a SHORTHAND, so it resets all four sides,
                         * and `ps-`/`pt-` utilities on this element are accepted
                         * and do nothing. Verified rather than reasoned: with
                         * `ps-[20px] pt-[14.5px]` literally in `className`, the
                         * element still computed `padding-inline-start: 6px` and
                         * `padding-top: 6px`, and enumerating every rule in the
                         * served sheets that both matches this node and sets
                         * padding returned that one rule alone.
                         */
                        'ps-(--facet-cat-row-pad)',
                        // The cap is a BG-search treatment and the token is
                        // declared only on a BG document, so an NM search rail
                        // resolves neither half and stays uncapped.
                        capChildList && 'overflow-auto max-h-(--facet-cat-list-max-h)'
                    )}>
                    {orderChildren(children).map((child) => (
                        <li
                            key={`${attributeId}:${child.value}`}
                            data-testid="refinement-cgid-child-row"
                            /* All three of NM's child-list measurements land
                               here, because the parent `<ul>` cannot carry the
                               first two — see the note on it.

                                 ps-[14px]        the indent. The `<ul>` is held
                                                  at 6px by that rule, and live's
                                                  parent label also sits 6px in,
                                                  so 6 + 14 puts the child text
                                                  the measured 14 CSS past its
                                                  parent's.
                                 first:mt-[14.5px] parent -> first child, 28 as
                                                  built + 14.5 = live's 42.5.
                                                  Weakest of the three: one
                                                  interval, not eight.
                                 mb-[22.5px]      16px line box + 22.5 = live's
                                                  38.5 pitch. A bottom margin
                                                  rather than a `row-gap`,
                                                  because this `<ul>` is a plain
                                                  block box and `gap` would be
                                                  inert on it — and because
                                                  live's own level-1 rows space
                                                  themselves with a uniform
                                                  `mb-[18px]` that the last row
                                                  carries too.

                               NOW TOKENS, AND NO LONGER GATED ON
                               `isExpanderMode`. The gate was `isRail &&
                               isSearch`, so NM's PLP rail — the same rail
                               component, `railVariant: 'rail'` via
                               `lib/category/plp-ux.ts` — reached NONE of these
                               and drew its child rows at a bare 16px pitch with
                               zero indent, rows literally touching. Measured at
                               1728: NM PLP child pitch 16.0 against NM SRP's
                               38.5, the `<li>` computing margin 0/0 and
                               padding-inline-start 0 where the SRP computes
                               14.5/22.5 and 14. That is the whole of the
                               "unstyled and bunched" report.

                               The three names are declared ONLY in
                               tokens/brand-nm/search.css, so the brand scoping
                               that `isExpanderMode` was doing by accident is now
                               done deliberately and by construction: on BG the
                               names resolve to nothing, each declaration is
                               invalid at computed-value time, and the property
                               falls to its initial 0 — which is exactly what BG
                               renders today (its 36px row comes from the inner
                               button's own 8px block padding, not from the
                               `<li>`). Verified rather than reasoned: BG's child
                               rows measure identically before and after. */
                            className="ps-(--facet-cat-child-indent) first:mt-(--facet-cat-child-space-top) mb-(--facet-cat-child-gap)">
                            <Button
                                variant="link"
                                onClick={() => toggleFilter(attributeId, child.value)}
                                className={cn(
                                    'justify-start',
                                    isFilterSelected(attributeId, child.value) && 'border-foreground/80',
                                    // Same invariant as the parent rows: on BG's search rail
                                    // the selected row is the bold one, wherever it sits.
                                    boldMeansSelected && isFilterSelected(attributeId, child.value) && 'font-bold'
                                )}>
                                {child.label || child.value}
                                {showCounts && child.hitCount !== undefined && (
                                    <span className="font-normal text-(length:--facet-cat-count-font-size) text-(--facet-cat-count-color) pl-(--facet-cat-count-pad-start)">
                                        ({child.hitCount})
                                    </span>
                                )}
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
}
